import { Num } from '../../../../utils/common'
import { logger } from '../../../../utils/common/logger'
import { DOMUtils } from '../../../../utils/dom'
import { Store } from '../../../../utils/storage/keys'
import {
    IIdentityFormFields,
    IIdentityLogoConfig,
    ISubscriberProviderConfig,
    ISubscriberServiceAdapter,
    ISubscription
} from '../adapter'
import { LoginService } from '../loginradius/services/login-service'
import { BasePianoSubscriberService, IPianoApiAccessItem, IPianoApiResponse } from './piano-base'
import regions from './regions.json'

interface IWindow extends Window {
    sessionStorage: any
    tp: any
    pianoCallback: (..._: any[]) => void
}

declare let window: IWindow

window.tp = window.tp || []

interface IPianoConfig {
    abTestEnabled?: boolean
    account?: string
    callback: string
    offset: number
    provider: string
}

interface IPianoRegion {
    rid: string,
    countryCode: string,
    regionCode: string
}

export class PianoLoginRadiusSubscriberService extends BasePianoSubscriberService implements ISubscriberServiceAdapter {
    private pianoConfig: IPianoConfig = {
        abTestEnabled: false,
        callback: 'pianoCallback',
        offset: 0,
        provider: 'loginradius'
    }

    constructor(private conf: ISubscriberProviderConfig,
                private logo: IIdentityLogoConfig,
                private isPremium = false,
                private isPaywallWhitelisted = false,
                isPianoAbTestEnabled = false
    ) {
        super()
        this.pianoConfig.account = conf.account
        this.pianoConfig.abTestEnabled = isPianoAbTestEnabled || false
    }

    /**
     * Sets JWT from Loginradius in Piano
     */
    public static SetExternalJWT() {
        const jwt = localStorage.getItem(Store.JWT)
        window.tp = window.tp || []
        window.tp.push(['init', () => {
            window.tp.push(['setExternalJWT', jwt])
        }])
        logger('info', 'Set Piano external JWT: ', jwt)
    }

    /**
     * Get subscriptions from Piano API using stored JWT from loginradius
     * @return Promise<ISubscription[]> - a promise that calls the piano API and resolves with an array of subscriptions
     */
    public async getAccess(): Promise<ISubscription[]> {
        if (!localStorage) {
            return undefined
        }
        this.userToken = localStorage.getItem(Store.JWT)

        const query = {
            aid: this.pianoConfig.account,
            cross_app: true,
            offset: this.pianoConfig.offset,
            user_provider: this.pianoConfig.provider,
            user_token: this.userToken
        }
        let pianoApiAccessItems: object[] = []
        try {
            const res = await this.pianoCallApi(this.pianoEndpoints.access, query)
            pianoApiAccessItems = res.data || []
        } catch (e) {
            logger('error', e)
        }
        return pianoApiAccessItems.map((item: IPianoApiAccessItem) => {
            const sub = {
                aid: item.resource.aid,
                id: item.resource.rid,
                start: item.start_date * Num.secToMs
            } as ISubscription

            // expiry not always present
            if (item.expire_date) {
                sub.end = item.expire_date * Num.secToMs
            }

            return sub
        })

    }

    public async getConversions(): Promise<string[]> {
        this.userToken = localStorage.getItem(Store.JWT)

        const query = {
            aid: this.conf.account,
            offset: this.pianoConfig.offset,
            user_provider: this.pianoConfig.provider,
            user_token: this.userToken
        }
        let conv
        try {
            conv = await this.pianoCallApi(this.pianoEndpoints.conversions, query)
        } catch(e) {
            logger('error', e)
        }
        const conversions = conv ? conv.conversions : []
        return (conversions).map((item) => {
            return item.term.term_id
        })
    }

    /**
     * Add the print subscription term to the user account
     * @return Promise<IPianoApiResponse>
     */
    public async addPrintSubscriptionTerm(formData: IIdentityFormFields): Promise<IPianoApiResponse> {
        const env = (this.conf.useSandbox)? 'sandbox' : 'production'
        const regionsList = regions[env] as IPianoRegion[]
        const region = regionsList.find((element: { rid: string }) => element.rid === formData.province)
        const fields = JSON.stringify({
            address1: formData.streetAddress,
            address2: formData.address2,
            city: formData.city,
            country: region.countryCode,
            firstName: formData.firstName,
            lastName: formData.lastName,
            phone: formData.phoneNumber,
            region: region.regionCode,
            zipCode: formData.postalCode
        })

        const query = {
            aid: this.conf.account,
            check_validity: true,
            fields,
            term_id: this.conf.activateTermId
        }

        return new Promise(async (resolve, reject) => {
            try {
                const response = await this.pianoCallApi(this.pianoEndpoints.externalCreate, query)
                resolve(response)
            } catch(e) {
                logger('error', 'Unable to add PSC term: ', e)
                reject(e)
            }
        })
    }

    public pianoOnInit(resolve: (value?: (PromiseLike<boolean> | boolean)) => void) {
        const redirectUrl = LoginService.getRedirectUrl('')
        this.addPianoHandlers('loginradius', this.pianoConfig.abTestEnabled)
        // TODO remove loginradius vendor arg when migration to LR is done
        if (window.tp.user.isUserValid() && redirectUrl && redirectUrl === location.href) {
            // check for sessionStorage and get piano params
            const pianoParams = window.sessionStorage ? sessionStorage.getItem(Store.PIANO_PARAMS) : undefined
            // If params object is valid - start checkout
            if (pianoParams) {
                window.tp.offer.startCheckout(JSON.parse(pianoParams))
                sessionStorage.removeItem(Store.PIANO_PARAMS)
            }
            LoginService.clearRedirectUrl()
        }
        resolve()
    }

    public async initialize() {
        this.pianoPreconfigured = !!window.tp
        this.registrantTermId = this.conf.registrantTermId || ''
        this.userToken = ''

        window.tp = window.tp || []

        // window.tp.push(['setEndpoint', 'https://sandbox.piano.io/api/v3'])
        window.tp.push(['setUseTinypassAccounts', false])
        window.tp.push(['setUsePianoIdUserProvider', false])
        window.tp.push(['setUsePianoIdLiteUserProvider', true])
        this.pianoPushLogoUrl(this.logo)

        await this.pianoPushTags(this.isPremium, this.isPaywallWhitelisted)

        // set ingestion of JWT tokens:
        if (localStorage && localStorage.getItem(Store.JWT)) {
            PianoLoginRadiusSubscriberService.SetExternalJWT()
        }
        // configure (promised as SSO session found event can happen before Piano init)
        this.pianoReady = new Promise((resolve) =>
            window.tp.push(['init', () => this.pianoOnInit(resolve)]))
        window.tp.push(['setAid', this.conf.account])

        // dev/stage only
        if (__NON_PROD) {
            window.tp.push(['setDebug', true])
        }

        // Add Piano aid to Piano experience script url
        if (this.conf.useSandbox) {
            this.pianoExpUrl = this.pianoSandboxUrl + this.conf.account
        } else {
            this.pianoExpUrl = this.pianoExpUrl + this.conf.account
        }

        // load Piano experience JS (includes both composer and API JS to browser)
        DOMUtils.loadScript(this.pianoExpUrl)

        // if loading /my-subscriptions page, tell piano to load the Manage widget
        await DOMUtils.ready
        if (document.getElementById('my-subscriptions')) {
            window.tp.push(['init', () => {
                window.tp.myaccount.show({
                    containerSelector: '#my-subscriptions',
                    displayMode: 'inline'
                })
            }])
        }
    }
}
